import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

import {
  channelLabel,
  formatLogTime,
  normalizeStep2Response,
  rosterStatusLabel,
  sourceLabel,
  stepLabel,
  type HwcgActivationAttempt,
  type HwcgActivationNoticeEntry,
  type HwcgActivationUserLog,
} from './hwcgActivationLogModel';

function attemptTone(attempt: HwcgActivationAttempt) {
  if (['failed', 'bounced', 'undeliverable'].includes(attempt.status)) {
    return 'destructive' as const;
  }
  if (attempt.respondedAt) return 'green' as const;
  if (attempt.deliveredAt) return 'blue' as const;
  return 'secondary' as const;
}

function attemptText(attempt: HwcgActivationAttempt): string {
  if (['failed', 'bounced', 'undeliverable'].includes(attempt.status)) {
    return `failed${attempt.error ? ` — ${attempt.error}` : ''}`;
  }
  if (attempt.respondedAt) {
    return `replied "${attempt.responseValue ?? ''}" ${formatLogTime(attempt.respondedAt)}`;
  }
  if (attempt.deliveredAt) return `delivered ${formatLogTime(attempt.deliveredAt)}`;
  if (attempt.sentAt) return `sent ${formatLogTime(attempt.sentAt)}`;
  return attempt.status;
}

function responseBadge(notice: HwcgActivationNoticeEntry) {
  if (!notice.response) {
    return (
      <Badge variant="amber" className="tw:font-normal">
        Awaiting reply
      </Badge>
    );
  }
  if (notice.step === 2) {
    const normalized = normalizeStep2Response(notice.response);
    return (
      <Badge
        variant={
          normalized === 'Activated'
            ? 'green'
            : normalized === 'Declined'
              ? 'destructive'
              : 'secondary'
        }
        className="tw:font-normal"
      >
        {notice.response} · {formatLogTime(notice.respondedAt)}
      </Badge>
    );
  }
  return (
    <Badge variant="green" className="tw:font-normal">
      {notice.response} · {formatLogTime(notice.respondedAt)}
    </Badge>
  );
}

function NoticeCard({ notice }: { notice: HwcgActivationNoticeEntry }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="tw:rounded-md tw:border tw:border-border tw:p-2.5">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2">
        <div className="tw:space-y-0.5">
          <p className="tw:m-0 tw:text-xs tw:font-semibold">
            {stepLabel(notice.step)}
          </p>
          <p className="tw:m-0 tw:text-[11px] tw:text-muted-foreground">
            {sourceLabel(notice.source)}
            {notice.positionName ? ` · ${notice.positionName}` : ''}
            {notice.groupId ? ` · ${notice.groupId.replace(/_/g, ' ')}` : ''}
            {' · sent '}
            {formatLogTime(notice.sentAt)}
          </p>
        </div>
        {responseBadge(notice)}
      </div>
      <ul className="tw:m-0 tw:mt-2 tw:flex tw:list-none tw:flex-wrap tw:gap-1.5 tw:p-0">
        {notice.attempts.map((attempt, index) => (
          <li key={`${attempt.channel}-${index}`}>
            <Badge variant={attemptTone(attempt)} className="tw:font-normal">
              {channelLabel(attempt.channel)}
              {attempt.destination ? ` ${attempt.destination}` : ''}: {attemptText(attempt)}
            </Badge>
          </li>
        ))}
        {notice.acknowledgedAt ? (
          <li>
            <Badge variant="green" className="tw:font-normal">
              Acknowledged in app {formatLogTime(notice.acknowledgedAt)}
            </Badge>
          </li>
        ) : null}
      </ul>
      {notice.script ? (
        <Collapsible open={open} onOpenChange={setOpen} className="tw:mt-2">
          <CollapsibleTrigger className="tw:inline-flex tw:items-center tw:gap-1 tw:text-[11px] tw:text-muted-foreground hover:tw:text-foreground">
            {open ? (
              <ChevronDown className="tw:size-3" />
            ) : (
              <ChevronRight className="tw:size-3" />
            )}
            {open ? 'Hide message' : 'Show message'}
          </CollapsibleTrigger>
          <CollapsibleContent>
            <p className="tw:m-0 tw:mt-1.5 tw:whitespace-pre-wrap tw:rounded tw:bg-muted/40 tw:p-2 tw:text-xs">
              {notice.script}
            </p>
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </li>
  );
}

/**
 * Everything one person was sent about activating for this incident, oldest
 * first, with each channel attempt and what they replied, plus where their
 * roster activation stands now.
 */
export function HwcgActivationUserTimeline({
  user,
  showHeader = true,
  emptyText = 'No activation notices have been sent to this person yet.',
}: {
  user: HwcgActivationUserLog | undefined;
  showHeader?: boolean;
  emptyText?: string;
}) {
  if (!user) {
    return (
      <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">{emptyText}</p>
    );
  }
  const statusTone =
    user.status === 'activated'
      ? 'green'
      : user.status === 'declined'
        ? 'destructive'
        : user.status === 'pending'
          ? 'amber'
          : 'secondary';
  return (
    <section
      className="tw:space-y-2"
      aria-label={`Activation log for ${user.name}`}
      data-tour="hwcg-activation-timeline"
    >
      {showHeader ? (
        <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2">
          <div>
            <p className="tw:m-0 tw:text-sm tw:font-semibold">{user.name}</p>
            <p className="tw:m-0 tw:text-[11px] tw:text-muted-foreground">
              {user.positions.length > 0
                ? user.positions.map((position) => position.positionName).join(', ')
                : user.isMember
                  ? 'No seat on the current roster'
                  : 'Not on the incident roster'}
            </p>
          </div>
          <Badge variant={statusTone} className="tw:font-normal">
            {rosterStatusLabel(user.status)}
            {user.activatedAt ? ` · ${formatLogTime(user.activatedAt)}` : ''}
            {user.declinedAt ? ` · ${formatLogTime(user.declinedAt)}` : ''}
          </Badge>
        </div>
      ) : null}
      {user.activationNote ? (
        <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
          Note: {user.activationNote}
        </p>
      ) : null}
      {user.notices.length === 0 ? (
        <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">{emptyText}</p>
      ) : (
        <ol className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-2 tw:p-0">
          {user.notices.map((notice) => (
            <NoticeCard key={notice.notificationId} notice={notice} />
          ))}
        </ol>
      )}
    </section>
  );
}

export default HwcgActivationUserTimeline;
